import { useMemo, useState } from 'react';

export interface Pagination<T> {
  /** Clamped current page (0-based). */
  page: number;
  /** Total number of pages (always >= 1). */
  pageCount: number;
  /** The items belonging to the current page. */
  pageItems: T[];
  /** Total item count across all pages. */
  total: number;
  /** 1-based index of the first item on the current page (0 when empty). */
  from: number;
  /** 1-based index of the last item on the current page. */
  to: number;
  setPage: (page: number) => void;
  /** Jump back to the first page — call when the underlying filter/sort changes. */
  reset: () => void;
}

/**
 * Client-side pagination over an already-filtered/sorted array. Owns the page
 * state and clamps it to the valid range, so callers just pass the current
 * dataset and render `pageItems` (plus a `<Pager>`).
 */
export function usePagination<T>(items: T[], pageSize: number): Pagination<T> {
  const [page, setPage] = useState(0);

  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
  const current = Math.min(page, pageCount - 1);

  const pageItems = useMemo(
    () => items.slice(current * pageSize, current * pageSize + pageSize),
    [items, current, pageSize],
  );

  const total = items.length;

  return {
    page: current,
    pageCount,
    pageItems,
    total,
    from: total === 0 ? 0 : current * pageSize + 1,
    to: Math.min(total, current * pageSize + pageSize),
    setPage,
    reset: () => setPage(0),
  };
}
